import { TTreeState } from '@infotech-soe/merps/types/Queries';
import {
  CategoryFieldName,
  CategoryName,
  DisplayModeName,
  TDatabaseId,
  TDatabaseRecord,
  TDatasetName,
  TSortQueryParams,
} from '@infotech-soe/metaschema';

import { DATASET_ID_PREFIX } from '../../constants/dataset';
import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { TRecordViewMap } from '../../models/user-config/DisplayModeConfig';
import CategoryViewModel from '../category/view-model/CategoryViewModel';
import CategoryViewModelSource from '../category/view-model/CategoryViewModelSource';
import DatasetViewModel from '../category/view-model/DatasetViewModel';
import UniqueId from '../uid/UniqueId';
import { DisplayModeConditions } from '../user-config/IUserConfigService';

import DisplayModeDatasetService from './display-mode/DisplayModeDatasetService';
import IDisplayModeDatasetService from './display-mode/IDisplayModeDatasetService';
import IDatasetService from './IDatasetService';
import { CreateDatasetResult } from './view-model/CreateDatasetResult';
import { DatasetCurrentState } from './view-model/DatasetCurrentState';
import { UpdateDatasetResult } from './view-model/UpdateDatasetResult';

class DatasetServiceSingleton implements IDatasetService {
  private readonly apiProvider: IApiProvider;
  private readonly displayModeDatasetService: IDisplayModeDatasetService;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
    this.displayModeDatasetService = new DisplayModeDatasetService();
  }

  public createDataset(
    source: CategoryViewModelSource,
    dataset: DatasetViewModel
  ): Promise<CreateDatasetResult> {
    return this.displayModeDatasetService.createDataset(source, dataset);
  }

  public updateDataset(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    conditions: DisplayModeConditions,
    treeState?: TTreeState,
    sort?: TSortQueryParams[]
  ): Promise<UpdateDatasetResult> {
    return this.displayModeDatasetService.updateDataset(
      category,
      datasetsState,
      datasetId,
      conditions,
      treeState,
      sort
    );
  }

  public async getById(
    source: CategoryName | DisplayModeName,
    id: TDatabaseId,
    field?: CategoryFieldName
  ): Promise<TDatabaseRecord | undefined> {
    return this.apiProvider.db.getById(source, id, field).catch(error => {
      if (error.code !== 1031) {
        throw error;
      }
      return undefined;
    });
  }

  public async getRecordById(
    category: CategoryName,
    displayMode: DisplayModeName,
    dataset: TDatasetName,
    id: TDatabaseId
  ): Promise<TDatabaseRecord | undefined> {
    return this.apiProvider.displayModes
      .getDatasetRecord(category, displayMode, dataset, id)
      .catch(error => {
        if (error.code !== 1031) {
          throw error;
        }
        return undefined;
      });
  }

  public focus(datasetId: UniqueId): void {
    document.getElementById(DATASET_ID_PREFIX + datasetId)?.focus();
  }

  public getHeader(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId
  ): Promise<string | undefined> {
    return this.displayModeDatasetService.getHeader(
      category,
      datasetsState,
      datasetId
    );
  }

  public getRecordViewMap(
    category: CategoryViewModel,
    datasetId: UniqueId
  ): Promise<TRecordViewMap | undefined> {
    return this.displayModeDatasetService.getRecordViewMap(category, datasetId);
  }
}

const DatasetService = (() => {
  let instance: IDatasetService;
  return new Proxy(DatasetServiceSingleton, {
    construct(Target: typeof DatasetServiceSingleton): IDatasetService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default DatasetService;
